import { useFormContext } from 'react-hook-form';
import { patchAppDataOpts, useApplication } from 'merchant-api/unified';
import BusinessContact, { BusinessContactSchema } from './BusinessContact';
import { BUTTERFLY_SENTINEL, FormattedMessageDiv, FormWrapper, LoadingAnimation, Navigation, useWizardContext } from '@odmonorepo/core-components';
import * as rfc6902 from 'rfc6902';
import * as yup from 'yup';
import { Stack } from 'react-bootstrap';
import { useMutation } from '@tanstack/react-query';
import { ensureSingleItem, initializeField } from '../businessFormHelpers';
import { hasProveBusinessPrefill } from '../helpers';

const buildBusinessContactSchema = () => {
  return yup.object().shape({
    party: yup.object().shape({
      businesses: BusinessContactSchema.fields.businesses,
    }),
  });
};

const formatData = (data) => {
  if (!data) return data;
  const formattedData = JSON.parse(JSON.stringify(data));

  formattedData.party.businesses = ensureSingleItem(formattedData.party.businesses, {
    contacts: [{ type: 'PHONE_NUMBER' }],
    addresses: [{ type: 'PHYSICAL_ADDRESS' }],
  });

  ['contacts', 'addresses'].forEach((field) => {
    const typeMap = { contacts: 'PHONE_NUMBER', addresses: 'PHYSICAL_ADDRESS' };
    initializeField(formattedData?.party?.businesses[0], field, typeMap[field]);
  });

  if (formattedData.party.businesses[0].addresses[0]) {
    formattedData.party.businesses[0].addresses[0].state = formattedData.party.businesses[0].addresses[0].state ?? '';
  }

  // Butterfly's create requires a business name, so applications started from the short form
  // are seeded with the applicant's own first + last name. That fallback is plumbing, not a
  // confirmed legal name — present the field empty so the user must type the real one.
  const person = formattedData.party.persons?.[0];
  const personNameFallback = [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim();
  const business = formattedData.party.businesses[0];
  if (business.legalName === BUTTERFLY_SENTINEL || (personNameFallback && business.legalName?.trim() === personNameFallback)) {
    business.legalName = '';
  }

  return formattedData;
};

const formatNewData = (applicationData, newData) => {
  const { party } = newData;
  const index = applicationData?.data.party?.businesses?.findIndex((data) => data.id === party?.businesses[0]?.id);
  newData.party.businesses = [
    ...applicationData?.data?.party.businesses.slice(0, index),
    party.businesses[0],
    ...applicationData?.data?.party.businesses.slice(index + 1),
  ];
  return newData;
};

export const BusinessContactStep = () => {
  const { applicationData, isLoadingApplication } = useApplication();

  if (isLoadingApplication)
    return (
      <div className="mx-auto">
        <LoadingAnimation />
      </div>
    );

  const schema = buildBusinessContactSchema();
  const defaultValues = formatData(applicationData?.data);
  const review = hasProveBusinessPrefill(applicationData ?? undefined);

  return (
    <FormWrapper schema={schema} defaultValues={defaultValues}>
      <Stack gap={review ? 5 : 6} className={review ? 'w-100 px-2 px-md-0' : undefined} style={{ maxWidth: '600px', margin: 'auto' }}>
        <FormattedMessageDiv className={review ? 'h3 text-center mb-0' : 'h3 text-center'} id={review ? 'businessReview.header' : 'renewalApp.step.1'} />
        <BusinessContactForm applicationData={applicationData} review={review} />
      </Stack>
    </FormWrapper>
  );
};

type BusinessContactFormProps = {
  applicationData: any;
  review?: boolean;
};

const BusinessContactForm = ({ applicationData, review }: BusinessContactFormProps) => {
  const { getValues } = useFormContext();
  const { nextStep, isFirstStep } = useWizardContext();
  const { mutateAsync } = useMutation({
    ...patchAppDataOpts(),
    onSuccess: () => nextStep(),
    onError: (error) => {
      throw error;
    },
  });

  const onNext = async () => {
    const newData = formatNewData(applicationData, getValues());
    const patches = rfc6902.createPatch(applicationData?.data, newData);
    await mutateAsync({ id: applicationData.id, patches });
  };

  const isStrategicPartner = applicationData?.metadata?.channelCode === 'PARTNER_STRATEGIC';

  return (
    <>
      <BusinessContact review={review}>
        <Navigation stacked continueOnly={isFirstStep()} handleNext={onNext} />
      </BusinessContact>
      {isStrategicPartner && (
        <FormattedMessageDiv
          className="fs-8"
          id="loanApp.disclaimer.tcpaConsent"
          values={{
            terms: (txt: any) => <a target="_blank" href="https://www.ondeck.com/terms">{txt}</a>,
            privacy: (txt: any) => <a target="_blank" href={`https://www.ondeck.com/privacy`}>{txt}</a>,
          }}
        />
      )}
    </>
  );
};

export default BusinessContactStep;
